import ours from "@oursprivacy/cdp-sdk";
import type { ComputedRef, Ref } from "vue";
import { mapTermlyToOurs, type TermlyConsentState } from "./ours/consent";
import { EventProps, sanitizeURLAndStripPHI, SENSITIVE_PARAM_SET } from "./ours/sanitize-props";
import { Member } from "@/api/members.api";

/**
 * Ours Privacy event names. Title-cased — the contract Ours uses for routing
 * to downstream destinations (Bing, Twitter, Meta CAPI, Google Ads, etc.).
 */
export const OursEvent = {
  Lead: "Lead",
  ViewItem: "View Item",
  FindLocation: "Find Location",
  AddToCart: "Add to Cart",
  BeginCheckout: "Begin Checkout",
  PurchaseComplete: "Purchase Complete",
  VirtualPageView: "Virtual Page View"
} as const;
export interface TrackingFlagsRefs {
  oursPrivacyId: Ref<string>;
  oursEnabled: ComputedRef<boolean>;
}

let _flags: TrackingFlagsRefs | undefined;
let _oursInitialized = false;

export function initTrackingFlags(flags: TrackingFlagsRefs): void {
  _flags = flags;
}

/** Test-only: clears the session guard and flag refs. */
export function _resetForTest(): void {
  _oursInitialized = false;
  _flags = undefined;
}

function getTermlyConsentState(): TermlyConsentState {
  const raw = window.Termly?.getConsentState?.();
  if (raw && typeof raw === "object") {
    return raw as TermlyConsentState;
  }
  return {};
}

/**
 * Initializes the Ours Privacy Web SDK when `ours-privacy-id` is active.
 * No-op when the flag is empty.
 */
export function initOP() {
  if (!_flags?.oursEnabled.value || _oursInitialized) return;

  const token = _flags.oursPrivacyId.value?.trim();
  if (!token) return;

  _oursInitialized = true;

  const isInternal =
    localStorage.getItem("is-internal-traffic") === "true" ? "1" : "0";

  try {
    ours.init(token, {
      track_web_events: true,
      url_query_params_blocklist: [...SENSITIVE_PARAM_SET],
      default_event_properties: { is_internal: isInternal },
      default_user_consent_properties: mapTermlyToOurs(getTermlyConsentState())
    });

    window.Termly?.on?.("consent", (consent: unknown) => {
      if (consent && typeof consent === "object") {
        updateConsentProperties(consent as TermlyConsentState);
      } else {
        updateConsentProperties({});
      }
    });
  } catch (e) {
    _oursInitialized = false;
    console.error("[tracking] Ours init error", e);
  }
}

/**
 * Identifies the current user with Ours (external_id only — no PHI).
 * No-op when Ours is disabled.
 */
export function identifyUser(member?: Member): void {
  if (!_flags?.oursEnabled.value || !_oursInitialized || !member?.id) return;
  try {
    // setting props, in "ours.identify", to null clears the value of the prop.
    // but setting the value to undefined or "" (empty string) leave the value unchanged.
    ours.identify({
      external_id: member.id,
      email: member.email ?? undefined,
      first_name: member.firstName ?? undefined,
      last_name: member.lastName ?? undefined,
      phone_number: member.phoneNumber ?? undefined
    });
  } catch (e) {
    console.error("[tracking] Ours identify error", e);
  }
}

/**
 * Clears Ours identity. No-op when Ours is disabled.
 */
export function resetTracking(): void {
  if (!_flags?.oursEnabled.value || !_oursInitialized) return;
  try {
    ours.reset();
  } catch (e) {
    console.error("[tracking] Ours reset error", e);
  }
}

/**
 * Pushes a consent update to Ours out-of-band of the Termly listener
 * registered in `initSdk()`. No-op when Ours is disabled.
 */
export function updateConsentProperties(
  consentState: TermlyConsentState | null | undefined
): void {
  if (!_flags?.oursEnabled.value || !_oursInitialized) return;
  try {
    ours.updateDefaultUserConsentProperties(
      mapTermlyToOurs(consentState ?? {})
    );
  } catch (e) {
    console.error("[tracking] Ours update consent error", e);
  }
}

export function setIsInternalEventProperty(isInternal: boolean): void {
  if (!_flags?.oursEnabled.value || !_oursInitialized) return;
  try {
    ours.updateDefaultEventProperties({ is_internal: isInternal ? "1" : "0" });
  } catch (e) {
    console.error("[tracking] Ours set is internal traffic error", e);
  }
}

/**
 * Base Event tracking called by the event specific tracking functions.
 * Sanitizes URL and PHI from the payload.
 */
function oursTrack(eventName: string, props: EventProps): void {
  if (!_flags?.oursEnabled.value || !_oursInitialized) return;
  try {
    ours.track(eventName, sanitizeURLAndStripPHI(props));
  } catch (e) {
    console.error("[tracking] Ours track error", eventName, e);
  }
}

/**
 * Event specific tracking functions
 */

// Lead event
export function trackLeadEvent(props: EventProps): void {
  oursTrack(OursEvent.Lead, props);
}

// View Item event
export function trackViewItemEvent(props: EventProps): void {
  oursTrack(OursEvent.ViewItem, props);
}

// Find Location
export function trackFindLocationEvent(props: EventProps): void {
  oursTrack(OursEvent.FindLocation, props);
}

// Add to Cart event
export function trackAddToCartEvent(props: EventProps): void {
  oursTrack(OursEvent.AddToCart, props);
}

// Begin Checkout event
export function trackBeginCheckoutEvent(props: EventProps): void {
  oursTrack(OursEvent.BeginCheckout, props);
}

// Purchase Complete event
export function trackPurchaseEvent(props: EventProps): void {
  oursTrack(OursEvent.PurchaseComplete, props);
}

// Virtual Page View event
export function trackVirtualPageViewEvent(props: EventProps): void {
  oursTrack(OursEvent.VirtualPageView, props);
}
